import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Upload, Link, Database, FileSpreadsheet, Globe, Check, X, 
  Loader2, AlertCircle, Package, ChevronRight, RefreshCw, Download,
  History, Edit3, Trash2, AlertTriangle, CheckCircle2, Info, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supermarket } from '@/lib/api';

const ProductImportTab = ({ onImportComplete }) => {
  const { t } = useTranslation();
  
  const PLATFORMS = [
    { id: 'woocommerce', name: 'WooCommerce', icon: '🛒', description: t('sellerPanel.import.woocommerceDesc') },
    { id: 'shopify', name: 'Shopify', icon: '🏪', description: t('sellerPanel.import.shopifyDesc') },
  ];
  
  const [activeMethod, setActiveMethod] = useState('csv');
  const [loading, setLoading] = useState(false);
  const [importedProducts, setImportedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [supportedSites, setSupportedSites] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Import results state
  const [importResults, setImportResults] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [errors, setErrors] = useState([]);
  
  // CSV/Excel state
  const [selectedFile, setSelectedFile] = useState(null);
  
  // URL state
  const [productUrl, setProductUrl] = useState('');
  const [importAll, setImportAll] = useState(false);
  const [detectedSite, setDetectedSite] = useState(null);
  
  // API state
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  
  // Bulk edit state
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditType, setBulkEditType] = useState('price');
  const [bulkEditValue, setBulkEditValue] = useState('');
  
  // Saving state
  const [saving, setSaving] = useState(false);

  // Load categories and supported sites on mount
  useEffect(() => {
    loadCategories();
    loadSupportedSites();
    loadImportHistory();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await supermarket.getImportCategories();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadSupportedSites = async () => {
    try {
      const response = await supermarket.getSupportedSites();
      setSupportedSites(response.data.sites || []);
    } catch (error) {
      console.error('Failed to load supported sites:', error);
    }
  };

  const loadImportHistory = async () => {
    try {
      const response = await supermarket.getImportHistory(10);
      setImportHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load import history:', error);
    }
  };

  // Download template
  const handleDownloadTemplate = async (format) => {
    try {
      const response = await supermarket.getImportTemplate(format);
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'csv' ? 'urun_import_sablonu.csv' : 'urun_import_sablonu.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('sellerPanel.import.templateDownloaded'));
    } catch (error) {
      toast.error(t('sellerPanel.import.templateDownloadFailed'));
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
        toast.error(t('sellerPanel.import.unsupportedFormat'));
        return;
      }
      setSelectedFile(file);
    }
  };

  // Import from CSV/Excel
  const handleCSVImport = async () => {
    if (!selectedFile) {
      toast.error(t('sellerPanel.import.selectFile'));
      return;
    }

    setLoading(true);
    setImportResults(null);
    setDuplicates([]);
    setErrors([]);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await supermarket.importFromCSV(formData);
      
      if (response.data.success) {
        setImportedProducts(response.data.products || []);
        setSelectedProducts(new Set(response.data.products?.map((_, i) => i) || []));
        setDuplicates(response.data.duplicates || []);
        setErrors(response.data.errors || []);
        setImportResults({
          total: response.data.total_products,
          unique: response.data.unique_products,
          duplicates: response.data.duplicate_count,
          errors: response.data.error_count
        });
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.import.unsupportedFormat'));
    } finally {
      setLoading(false);
    }
  };

  // Detect site from URL
  const detectSiteFromUrl = (url) => {
    const site = supportedSites.find(s => url.toLowerCase().includes(s.domain));
    setDetectedSite(site);
    return site;
  };

  // Import from URL
  const handleURLImport = async () => {
    if (!productUrl) {
      toast.error(t('sellerPanel.import.enterUrl'));
      return;
    }

    setLoading(true);
    setImportResults(null);
    setDuplicates([]);
    
    try {
      const response = await supermarket.importFromURL({ url: productUrl, import_all: importAll });
      
      if (response.data.success) {
        setImportedProducts(response.data.products || []);
        setSelectedProducts(new Set(response.data.products?.map((_, i) => i) || []));
        setDuplicates(response.data.duplicates || []);
        setImportResults({
          total: response.data.total_products,
          unique: response.data.unique_products,
          duplicates: response.data.duplicate_count,
          siteType: response.data.site_type
        });
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.import.enterUrl'));
    } finally {
      setLoading(false);
    }
  };

  // Import from API
  const handleAPIImport = async () => {
    if (!selectedPlatform || !storeUrl) {
      toast.error(t('sellerPanel.import.platformAndUrlRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await supermarket.importFromAPI({
        platform: selectedPlatform,
        store_url: storeUrl,
        api_key: apiKey,
        api_secret: apiSecret,
        access_token: accessToken,
        limit: 100
      });
      
      if (response.data.success) {
        setImportedProducts(response.data.products || []);
        setSelectedProducts(new Set(response.data.products?.map((_, i) => i) || []));
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.import.platformAndUrlRequired'));
    } finally {
      setLoading(false);
    }
  };

  // Toggle product selection
  const toggleProduct = (index) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedProducts(newSelected);
  };

  // Select/Deselect all
  const toggleAll = () => {
    if (selectedProducts.size === importedProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(importedProducts.map((_, i) => i)));
    }
  };

  // Update product category
  const updateProductCategory = (index, categoryId, subcategory) => {
    const updatedProducts = [...importedProducts];
    updatedProducts[index] = {
      ...updatedProducts[index],
      category: categoryId,
      subcategory: subcategory
    };
    setImportedProducts(updatedProducts);
  };

  // Update product field
  const updateProductField = (index, field, value) => {
    const updatedProducts = [...importedProducts];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value
    };
    setImportedProducts(updatedProducts);
  };

  // Bulk edit products
  const handleBulkEdit = () => {
    const selectedIndices = Array.from(selectedProducts);
    const updatedProducts = [...importedProducts];
    
    selectedIndices.forEach(index => {
      if (bulkEditType === 'price_percent') {
        const currentPrice = updatedProducts[index].price || 0;
        const change = parseFloat(bulkEditValue) || 0;
        updatedProducts[index].price = Math.round(currentPrice * (1 + change / 100) * 100) / 100;
      } else if (bulkEditType === 'price') {
        updatedProducts[index].price = parseFloat(bulkEditValue) || 0;
      } else if (bulkEditType === 'stock') {
        updatedProducts[index].stock = parseInt(bulkEditValue) || 0;
      } else if (bulkEditType === 'category') {
        updatedProducts[index].category = bulkEditValue;
      }
    });
    
    setImportedProducts(updatedProducts);
    setShowBulkEdit(false);
    setBulkEditValue('');
    toast.success(`${selectedIndices.length} ürün güncellendi`);
  };

  // Save selected products
  const handleSave = async () => {
    const productsToSave = importedProducts.filter((_, i) => selectedProducts.has(i));
    
    if (productsToSave.length === 0) {
      toast.error(t('sellerPanel.import.selectAtLeastOne'));
      return;
    }

    setSaving(true);
    try {
      const response = await supermarket.saveImportedProducts(productsToSave);
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        if (response.data.error_count > 0) {
          toast.warning(`${response.data.error_count} ürün kaydedilemedi`);
        }
        
        // Clear imported products and refresh
        setImportedProducts([]);
        setSelectedProducts(new Set());
        setSelectedFile(null);
        setProductUrl('');
        setImportResults(null);
        setDuplicates([]);
        setErrors([]);
        
        // Refresh history
        loadImportHistory();
        
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sellerPanel.import.selectAtLeastOne'));
    } finally {
      setSaving(false);
    }
  };

  // Delete import batch
  const handleDeleteImport = async (importId, deleteProducts = false) => {
    try {
      await supermarket.deleteImportBatch(importId, deleteProducts);
      toast.success('Import kaydı silindi');
      loadImportHistory();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  return (
    <div className="space-y-6">
      {/* Import Methods */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                {t('sellerPanel.import.title')}
              </CardTitle>
              <CardDescription>
                {t('sellerPanel.import.description')}
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowHistory(!showHistory)}
            >
              <History className="w-4 h-4 mr-2" />
              {t('sellerPanel.import.history')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Import History Panel */}
          {showHistory && importHistory.length > 0 && (
            <div className="mb-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                {t('sellerPanel.import.recentImports')}
              </h4>
              <div className="space-y-2">
                {importHistory.map((record) => (
                  <div key={record.id} className="flex items-center justify-between p-3 bg-background rounded border">
                    <div className="flex items-center gap-3">
                      <Badge variant={record.status === 'completed' ? 'default' : 'secondary'}>
                        {record.import_type}
                      </Badge>
                      <span className="text-sm">
                        {record.saved_count} ürün • {new Date(record.created_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteImport(record.id, false)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Tabs value={activeMethod} onValueChange={setActiveMethod}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="csv" className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                {t('sellerPanel.import.csvExcel')}
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link className="w-4 h-4" />
                {t('sellerPanel.import.urlImport')}
              </TabsTrigger>
              <TabsTrigger value="api" className="flex items-center gap-2">
                <Database className="w-4 h-4" />
                {t('sellerPanel.import.apiIntegration')}
              </TabsTrigger>
            </TabsList>

            {/* CSV/Excel Import */}
            <TabsContent value="csv" className="space-y-4 mt-4">
              {/* Template Download */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Info className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-800 flex-1">
                  {t('sellerPanel.import.downloadTemplateHint')}
                </span>
                <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('csv')}>
                  <Download className="w-4 h-4 mr-1" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('excel')}>
                  <Download className="w-4 h-4 mr-1" />
                  Excel
                </Button>
              </div>

              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  {t('sellerPanel.import.uploadDesc')}
                  <br />
                  <span className="font-medium">{t('sellerPanel.import.columns')}</span>
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button variant="outline" asChild>
                    <span className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      {t('sellerPanel.import.uploadFile')}
                    </span>
                  </Button>
                </label>
                {selectedFile && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ {selectedFile.name}
                  </p>
                )}
              </div>
              <Button 
                onClick={handleCSVImport} 
                disabled={!selectedFile || loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('sellerPanel.import.uploading')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {t('sellerPanel.import.importFile')}
                  </>
                )}
              </Button>
            </TabsContent>

            {/* URL Import */}
            <TabsContent value="url" className="space-y-4 mt-4">
              {/* Supported Sites */}
              {supportedSites.length > 0 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 mb-2 font-medium">
                    {t('sellerPanel.import.supportedSites')}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {supportedSites.map(site => (
                      <Badge key={site.id} variant="outline" className="text-green-700">
                        {site.icon} {site.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label>{t('sellerPanel.import.productUrl')}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder={t('sellerPanel.import.urlPlaceholder')}
                      value={productUrl}
                      onChange={(e) => {
                        setProductUrl(e.target.value);
                        detectSiteFromUrl(e.target.value);
                      }}
                      className="flex-1"
                    />
                    {detectedSite && (
                      <Badge variant="default" className="self-center">
                        {detectedSite.icon} {detectedSite.name}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{t('sellerPanel.import.scanEntireSite')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('sellerPanel.import.scanEntireSiteDesc')}
                    </p>
                  </div>
                  <Switch
                    checked={importAll}
                    onCheckedChange={setImportAll}
                  />
                </div>
              </div>
              <Button 
                onClick={handleURLImport} 
                disabled={!productUrl || loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('sellerPanel.import.scanning')}
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 mr-2" />
                    {t('sellerPanel.import.importFromUrl')}
                  </>
                )}
              </Button>
            </TabsContent>

            {/* API Import */}
            <TabsContent value="api" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                {PLATFORMS.map((platform) => (
                  <div
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedPlatform === platform.id 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:border-muted-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{platform.icon}</span>
                      <div>
                        <p className="font-medium">{platform.name}</p>
                        <p className="text-sm text-muted-foreground">{platform.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedPlatform && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>{t('sellerPanel.import.storeUrl')}</Label>
                    <Input
                      placeholder={selectedPlatform === 'shopify' 
                        ? 'https://your-store.myshopify.com' 
                        : 'https://your-store.com'
                      }
                      value={storeUrl}
                      onChange={(e) => setStoreUrl(e.target.value)}
                    />
                  </div>
                  
                  {selectedPlatform === 'woocommerce' && (
                    <>
                      <div>
                        <Label>{t('sellerPanel.import.consumerKey')}</Label>
                        <Input
                          placeholder="ck_xxxxxxxxxxxxx"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>{t('sellerPanel.import.consumerSecret')}</Label>
                        <Input
                          type="password"
                          placeholder="cs_xxxxxxxxxxxxx"
                          value={apiSecret}
                          onChange={(e) => setApiSecret(e.target.value)}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('sellerPanel.import.woocommerceHelp')}
                      </p>
                    </>
                  )}
                  
                  {selectedPlatform === 'shopify' && (
                    <>
                      <div>
                        <Label>{t('sellerPanel.import.accessToken')}</Label>
                        <Input
                          type="password"
                          placeholder="shpat_xxxxxxxxxxxxx"
                          value={accessToken}
                          onChange={(e) => setAccessToken(e.target.value)}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('sellerPanel.import.shopifyHelp')}
                      </p>
                    </>
                  )}
                </div>
              )}

              <Button 
                onClick={handleAPIImport} 
                disabled={!selectedPlatform || !storeUrl || loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('sellerPanel.import.connecting')}
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    {t('sellerPanel.import.fetchProducts')}
                  </>
                )}
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Import Results Summary */}
      {importResults && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{importResults.total}</p>
              <p className="text-sm text-blue-600">{t('sellerPanel.import.totalRead')}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{importResults.unique}</p>
              <p className="text-sm text-green-600">{t('sellerPanel.import.uniqueProducts')}</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-700">{importResults.duplicates || 0}</p>
              <p className="text-sm text-yellow-600">{t('sellerPanel.import.duplicates')}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{importResults.errors || 0}</p>
              <p className="text-sm text-red-600">{t('sellerPanel.import.errors')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Errors Alert */}
      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('sellerPanel.import.importErrors')}</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1 text-sm">
              {errors.slice(0, 5).map((err, i) => (
                <li key={i}>Satır {err.row}: {err.error}</li>
              ))}
              {errors.length > 5 && <li>...ve {errors.length - 5} hata daha</li>}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Duplicates Alert */}
      {duplicates.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('sellerPanel.import.duplicatesFound')}</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1 text-sm">
              {duplicates.slice(0, 3).map((dup, i) => (
                <li key={i}>{dup.name}: {dup.duplicate_reason}</li>
              ))}
              {duplicates.length > 3 && <li>...ve {duplicates.length - 3} tekrar daha</li>}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Imported Products Preview */}
      {importedProducts.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {t('sellerPanel.import.importedProducts')} ({importedProducts.length})
                </CardTitle>
                <CardDescription>
                  {t('sellerPanel.import.importedProductsDesc')}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={toggleAll}>
                  {selectedProducts.size === importedProducts.length ? t('sellerPanel.import.deselectAll') : t('sellerPanel.import.selectAll')}
                </Button>
                
                {/* Bulk Edit Button */}
                <Dialog open={showBulkEdit} onOpenChange={setShowBulkEdit}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" disabled={selectedProducts.size === 0}>
                      <Edit3 className="w-4 h-4 mr-1" />
                      {t('sellerPanel.import.bulkEdit')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('sellerPanel.import.bulkEditTitle')}</DialogTitle>
                      <DialogDescription>
                        {selectedProducts.size} ürün seçili
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>{t('sellerPanel.import.editType')}</Label>
                        <Select value={bulkEditType} onValueChange={setBulkEditType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="price_percent">{t('sellerPanel.import.priceChangePercent')}</SelectItem>
                            <SelectItem value="price">{t('sellerPanel.import.setPrice')}</SelectItem>
                            <SelectItem value="stock">{t('sellerPanel.import.setStock')}</SelectItem>
                            <SelectItem value="category">{t('sellerPanel.import.setCategory')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t('sellerPanel.import.newValue')}</Label>
                        {bulkEditType === 'category' ? (
                          <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('sellerPanel.import.selectCategory')} />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type="number"
                            placeholder={bulkEditType === 'price_percent' ? '+10 veya -10' : '0'}
                            value={bulkEditValue}
                            onChange={(e) => setBulkEditValue(e.target.value)}
                          />
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowBulkEdit(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button onClick={handleBulkEdit}>
                        {t('sellerPanel.import.applyChanges')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                <Badge variant="secondary">
                  {selectedProducts.size} {t('sellerPanel.import.selected')}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {importedProducts.map((product, index) => (
                  <div 
                    key={index}
                    className={`p-4 border rounded-lg transition-all ${
                      selectedProducts.has(index) ? 'border-primary bg-primary/5' : ''
                    } ${product.has_warning ? 'border-yellow-400' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedProducts.has(index)}
                        onCheckedChange={() => toggleProduct(index)}
                      />
                      
                      {product.image_url && (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium truncate">{product.name}</h4>
                          {product.has_warning && (
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          )}
                          {product.source_type && product.source_type !== 'generic' && (
                            <Badge variant="outline" className="text-xs">
                              {product.source_type}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {product.description?.slice(0, 100)}
                        </p>
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{t('sellerPanel.import.price')}:</span>
                            <Input
                              type="number"
                              value={product.price || ''}
                              onChange={(e) => updateProductField(index, 'price', parseFloat(e.target.value) || 0)}
                              className="w-24 h-7 text-sm"
                            />
                            <span className="text-sm">₺</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{t('sellerPanel.import.stock')}:</span>
                            <Input
                              type="number"
                              value={product.stock || ''}
                              onChange={(e) => updateProductField(index, 'stock', parseInt(e.target.value) || 0)}
                              className="w-20 h-7 text-sm"
                            />
                          </div>
                          {product.brand && (
                            <Badge variant="outline" className="text-xs">
                              {product.brand}
                            </Badge>
                          )}
                          {product.original_category && (
                            <Badge variant="outline" className="text-xs">
                              {t('sellerPanel.import.originalCategory')}: {product.original_category}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <Select
                          value={product.category || product.suggested_category?.category_id || ''}
                          onValueChange={(value) => {
                            const cat = categories.find(c => c.id === value);
                            updateProductCategory(index, value, cat?.subcategories?.[0] || '');
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder={t('sellerPanel.import.selectCategory')} />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {product.suggested_category && (
                          <div className="flex items-center gap-1 text-xs">
                            <Badge 
                              variant={product.suggested_category.confidence === 'high' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {t('sellerPanel.import.aiSuggested')}: {getCategoryName(product.suggested_category.category_id)}
                            </Badge>
                            <span className="text-muted-foreground">
                              ({product.suggested_category.confidence})
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            <div className="flex justify-end gap-4 mt-6 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setImportedProducts([]);
                  setSelectedProducts(new Set());
                  setImportResults(null);
                  setDuplicates([]);
                  setErrors([]);
                }}
              >
                <X className="w-4 h-4 mr-2" />
                {t('sellerPanel.import.cancel')}
              </Button>
              <Button 
                onClick={handleSave}
                disabled={selectedProducts.size === 0 || saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('sellerPanel.import.savingProducts')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {selectedProducts.size} {t('sellerPanel.import.saveProducts')}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductImportTab;
